import React, { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import CalendarPicker from "./calendar";
import { Field } from "..";
import TimePicker from "./time-picker";
import useLanguage from "../../hooks/language";

import styles from "./datetime-select.module.css";
import { formatSummaryTime } from "./format-selection";

const DEFAULT_PICKER_MAX_HEIGHT = 500;

interface DatetimePickerProps {
    className?: string;
    openedDates?: string[];
    availableTimeSlots?: string[];
    date?: Date | undefined;
    minDate?: Date;
    maxDate?: Date;
    onDateChange?: (date: Date) => void;
    onTimeChange?: (time: string | undefined) => void;
    onCalendarNavigation?: (firstActiveDay: Date) => void;
}

const DatetimePicker: React.FunctionComponent<DatetimePickerProps> = ({
    className,
    openedDates,
    availableTimeSlots,
    date,
    minDate,
    maxDate,
    onDateChange,
    onTimeChange,
    onCalendarNavigation,
}: DatetimePickerProps) => {
    const { language, translations } = useLanguage();
    const t = translations.dateSelectorComponent;

    // Refs

    const calendarColRef = useRef<HTMLDivElement>(null);

    // States

    const [_selectedDate, setSelectedDate] = useState<Date | undefined>(date);
    const [selectedTime, setSelectedTime] = useState<string>();

    // Effects

    useEffect(() => {
        if (onDateChange && _selectedDate) onDateChange(_selectedDate);
    }, [_selectedDate]);

    useEffect(() => {
        if (date !== _selectedDate) {
            setSelectedDate(date);
        }
    }, [date]);

    useEffect(() => {
        setSelectedTime(undefined);
    }, [_selectedDate, availableTimeSlots]);

    // Handlers

    const handleTimeChange = (time: string | undefined) => {
        setSelectedTime(time);
        if (onTimeChange) {
            onTimeChange(time);
        }
    };

    // Rendering

    const dateString = _selectedDate
        ? DateTime.fromJSDate(_selectedDate)
              .setLocale(language)
              .toLocaleString(DateTime.DATE_MED)
        : "";

    const maxTimePickerHeight =
        calendarColRef !== null && calendarColRef.current
            ? calendarColRef.current.clientHeight
            : DEFAULT_PICKER_MAX_HEIGHT;

    const formattedSelectedTime = selectedTime
        ? formatSummaryTime(selectedTime, language)
        : undefined;

    const showScrollHint =
        availableTimeSlots !== undefined && availableTimeSlots.length > 0;

    return (
        <div className={`${styles.holder} ${className ?? ""}`}>
            <div className={`${styles.calendarCol}`} ref={calendarColRef}>
                <Field
                    placeholder={t.choseDateLabel}
                    value={dateString}
                    readOnly={true}
                />
                <div className={styles.calendarHolder}>
                    <CalendarPicker
                        minDate={minDate}
                        maxDate={maxDate}
                        onDateChange={setSelectedDate}
                        openedDates={openedDates}
                        onCalendarNavigation={onCalendarNavigation}
                    />
                </div>
            </div>
            <div className={`${styles.timeCol}`}>
                <div className={styles.timeLabelRow}>
                    <span className={styles.timeLabel}>{t.choseHourLabel}</span>
                    {formattedSelectedTime && (
                        <span className={styles.selectedTimeValue}>
                            {formattedSelectedTime}
                        </span>
                    )}
                </div>
                <TimePicker
                    maxHeight={maxTimePickerHeight}
                    onSelectTime={handleTimeChange}
                    labelText={t.choseHourLabel}
                    availableTimeSlots={availableTimeSlots}
                />
                {showScrollHint && (
                    <p className={styles.scrollHint}>{t.scrollTimesHint}</p>
                )}
            </div>
        </div>
    );
};
export default DatetimePicker;
